import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, CreditCard, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, label, fmtDate } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader, Panel, Spinner, Empty, IconBox } from "../../components/kit";
import { Button } from "../../components/ui/button";

const rand = (n) => `R${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const useClubId = () => useAuth().me.memberships.find((m) => m.role === "club_admin" && m.status === "active")?.org_id;

const payWithPayFast = ({ action, fields }) => {
  const f = document.createElement("form");
  f.method = "POST";
  f.action = action;
  Object.entries(fields).forEach(([name, value]) => {
    const i = document.createElement("input");
    i.type = "hidden";
    i.name = name;
    i.value = value;
    f.appendChild(i);
  });
  document.body.appendChild(f);
  f.submit();
};

const PlanCard = ({ plan, current, locked, busy, onPay }) => (
  <div className={`tro-card flex flex-col p-4 ${current ? "border-2 border-[#171B20]" : ""}`} data-testid={`plan-${plan.id}`}>
    <div className="flex items-center gap-3"><IconBox icon={CreditCard} active={current} /><div><div className="text-lg font-extrabold">{plan.name}</div><div className="font-mono text-2xl font-extrabold" data-testid={`plan-price-${plan.id}`}>{rand(plan.amount)}<span className="text-sm font-semibold text-[#6C757D]">/month</span></div></div></div>
    <ul className="my-4 space-y-1.5 text-sm">{plan.features.map((f) => <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#35865C]" />{f}</li>)}</ul>
    <Button className="mt-auto h-12 rounded-[14px]" disabled={busy || locked} data-testid={`pay-${plan.id}-btn`} onClick={() => onPay(plan.id)}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{current ? "Active · renews automatically" : locked ? "Another plan is active" : "Subscribe with PayFast"}
    </Button>
  </div>
);

export function ClubSubscription() {
  const cid = useClubId();
  const [plans, setPlans] = useState(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/payments/plans").then((r) => setPlans(r.data));
    if (cid) api.get(`/payments/club/${cid}`).then((r) => setD(r.data));
  }, [cid]);
  if (!cid) return <Empty text="Only club administrators can manage the subscription." />;
  if (!plans || !d) return <Spinner />;
  const pay = async (plan) => {
    setBusy(true);
    try { payWithPayFast((await api.post("/payments/checkout", { plan, club_id: cid, origin_url: window.location.origin })).data); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const sub = d.subscription;
  const paidActive = sub?.provider === "payfast" && sub?.status === "active";
  return (
    <div className="space-y-4" data-testid="club-subscription">
      <PageHeader title="Subscription" sub="Monthly TRO club plan · prices in South African Rand" />
      {plans.sandbox && <div className="rounded-[14px] bg-[#FFD400]/20 px-3 py-2 text-xs font-bold text-[#6b5600]" data-testid="payfast-test-mode">PayFast test mode — no real money moves. Use the PayFast sandbox wallet to complete payment.</div>}
      <div className="tro-card flex items-center gap-3 p-4" data-testid="current-subscription">
        <IconBox icon={ShieldCheck} active={sub?.status === "active"} />
        <div className="min-w-0 flex-1"><div className="tro-label">Current plan</div><div className="text-lg font-extrabold">{sub?.plan || "No plan"}</div><div className="text-xs text-[#6C757D]">{sub ? `${label(sub.status)} \u00b7 renews ${sub.renews}` : "Choose a plan below"}</div></div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{plans.plans.map((p) => <PlanCard key={p.id} plan={p} current={sub?.plan_id === p.id && sub?.status === "active"} locked={paidActive} busy={busy} onPay={pay} />)}</div>
      <Panel title="Payment history" testid="payment-history">
        {d.transactions.length ? d.transactions.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-2 border-b border-[#E7EAEE] py-2 text-sm last:border-0" data-testid={`payment-${t.id}`}>
            <div className="min-w-0"><b>{t.plan === "pro" ? "Club Pro" : "Club Standard"}</b> · {rand(t.amount)}<div className="text-xs text-[#6C757D]">{fmtDate(t.created_at)} · {t.id}</div></div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${t.payment_status === "paid" ? "bg-[#35865C] text-white" : "bg-[#E7EAEE]"}`}>{label(t.payment_status)}</span>
          </div>
        )) : <Empty text="No payments yet." />}
      </Panel>
    </div>
  );
}

export function PaymentResult() {
  const [sp] = useSearchParams();
  const ref = sp.get("ref");
  const cancelled = sp.get("cancelled");
  const [st, setSt] = useState(cancelled ? "cancelled" : "checking");
  useEffect(() => {
    if (!ref) return undefined;
    if (cancelled) { api.post(`/payments/cancel/${ref}`).catch(() => {}); return undefined; }
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const r = await api.get(`/payments/status/${ref}`);
        if (r.data.payment_status === "paid") { setSt("paid"); clearInterval(t); }
        else if (["failed", "cancelled"].includes(r.data.payment_status)) { setSt(r.data.payment_status); clearInterval(t); }
      } catch { /* keep polling */ }
      if (n >= 15) { setSt((s) => (s === "checking" ? "pending" : s)); clearInterval(t); }
    }, 2000);
    return () => clearInterval(t);
  }, [ref, cancelled]);
  const view = {
    checking: [Loader2, "Confirming your payment with PayFast\u2026", "This usually takes a few seconds."],
    paid: [ShieldCheck, "Payment received", "Your club subscription is active."],
    pending: [Loader2, "Still waiting for PayFast", "We'll activate the plan as soon as PayFast confirms the payment. You can check the Subscription page later."],
    cancelled: [XCircle, "Payment cancelled", "No money was taken. You can choose a plan again any time."],
    failed: [XCircle, "Payment failed", "PayFast could not complete the payment. Please try again."],
  }[st];
  const [I, title, body] = view;
  return (
    <div className="mx-auto max-w-md space-y-4 py-8 text-center" data-testid="payment-result">
      <div className="flex justify-center"><IconBox icon={I} active={st === "paid"} tone={["cancelled", "failed"].includes(st) ? "danger" : undefined} className={st === "checking" ? "[&_svg]:animate-spin" : ""} /></div>
      <h1 className="text-2xl font-extrabold" data-testid="payment-result-title">{title}</h1>
      <p className="text-sm text-[#6C757D]">{body}</p>
      <Link to="/club/subscription"><Button className="h-12 w-full rounded-[14px]" data-testid="back-to-subscription-btn">Back to subscription</Button></Link>
    </div>
  );
}
